import { useState } from 'react';
import clsx from 'clsx';
import { useKlantenStore } from '../../stores/klanten-store';

export interface EmailConsentTarget {
  recordType: 'lead' | 'account' | 'contact';
  recordId: string;
  /** Naam van de klant/contactpersoon voor in de kop. */
  label: string;
  currentlyOptedOut: boolean;
}

interface EmailConsentDialogProps {
  target: EmailConsentTarget | null;
  onClose: () => void;
  /** Optioneel — store werkt al optimistisch bij, dus meestal niet nodig. */
  onChanged?: () => void;
}

/**
 * Bevestigingsdialog voor de marketing-opt-out. Spiegelt OpFactuurToggleDialog
 * maar de notitie is optioneel (laagdrempelige, omkeerbare actie). Primary-knop
 * = bg-indigo-600 (niet bg-cinco-primary).
 */
export function EmailConsentDialog({ target, onClose, onChanged }: EmailConsentDialogProps) {
  const setEmailConsent = useKlantenStore((s) => s.setEmailConsent);
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!target) return null;

  const nextValue = !target.currentlyOptedOut;

  const close = () => {
    setReden('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const res = await setEmailConsent(target.recordType, target.recordId, nextValue, reden.trim() || undefined);
    setSubmitting(false);
    if (res.ok) {
      onChanged?.();
      close();
    } else {
      setError(res.error);
    }
  };

  const title = nextValue ? 'Marketing-mails uitzetten' : 'Marketing-mails weer aanzetten';
  const submitLabel = nextValue ? 'Zet marketing-mails uit' : 'Zet marketing-mails aan';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 mt-1">{target.label}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {nextValue ? (
            <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 space-y-1">
              <p className="font-semibold text-slate-800">Wat stopt er:</p>
              <p>Marketing-mails: nieuwsbrief, follow-ups, upsell en reviewverzoeken.</p>
              <p className="text-slate-500">
                Orderbevestiging, factuur en bezorgmeldingen blijven gewoon gaan.
              </p>
            </div>
          ) : (
            <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              Klant ontvangt weer marketing-mails (nieuwsbrief, follow-ups, upsell, reviewverzoeken).
            </div>
          )}

          <label className="block">
            <span className="text-sm text-slate-700">Notitie (optioneel)</span>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={2}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder="Bv. telefonisch verzoek, klant wil geen nieuwsbrief"
              disabled={submitting}
            />
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Bezig…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
